import React from 'react';
import { Heart, Users, TrendingUp, DollarSign, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Donate = () => {
  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Heart className="w-16 h-16 mx-auto mb-6 text-white" fill="white" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Support Our Mission
            </h1>
            <p className="text-xl text-orange-100 leading-relaxed mb-6">
              Your donation transforms lives. David's Harmony House Foundation is a registered 501(c)(3) nonprofit organization. 
              All donations are tax-deductible and 100% go directly to supporting our programs.
            </p>
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
              <CheckCircle className="w-5 h-5" />
              <p className="text-sm font-medium">
                501(c)(3) Approved • Tax-Deductible Donations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Form Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
              {/* Zeffy Iframe Embed */}
              <div className="relative w-full" style={{ minHeight: '800px' }}>
                <iframe
                  src="https://www.zeffy.com/en-US/embed/donation-form/donate-to-change-lives-20747"
                  title="David's Harmony House Foundation - Donation Form"
                  className="w-full h-full"
                  style={{
                    border: 'none',
                    minHeight: '800px',
                    height: '100vh'
                  }}
                  allowFullScreen
                  allow="payment"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Preview */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Your Impact
              </h2>
              <p className="text-xl text-gray-600">
                See how your donation makes a real difference
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              <Card className="border-0 shadow-lg text-center hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <DollarSign className="w-10 h-10 text-orange-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-gray-900 mb-2">$50</div>
                  <div className="text-sm text-gray-600">Provides 1 week of meals</div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg text-center hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <Users className="w-10 h-10 text-orange-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-gray-900 mb-2">$100</div>
                  <div className="text-sm text-gray-600">Supports job training for 1 person</div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg text-center hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <Heart className="w-10 h-10 text-orange-600 mx-auto mb-3" fill="currentColor" />
                  <div className="text-3xl font-bold text-gray-900 mb-2">$250</div>
                  <div className="text-sm text-gray-600">Covers 1 month of case management</div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg text-center hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <TrendingUp className="w-10 h-10 text-orange-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-gray-900 mb-2">$500</div>
                  <div className="text-sm text-gray-600">Provides transitional housing support</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Help */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                More Ways to <span className="text-orange-600">Help</span>
              </h2>
              <p className="text-xl text-gray-600">
                Beyond donations, you can make a difference in other ways
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                <CardContent className="p-8">
                  <Users className="w-12 h-12 text-orange-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Volunteer Your Time</h3>
                  <p className="text-gray-600 mb-6">
                    Join our team of dedicated volunteers making a direct impact in the lives 
                    of veterans, seniors, youth, and individuals rebuilding their lives.
                  </p>
                  <a href="/get-involved">
                    <button className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl">
                      Volunteer Application
                    </button>
                  </a>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                <CardContent className="p-8">
                  <Heart className="w-12 h-12 text-orange-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Spread the Word</h3>
                  <p className="text-gray-600 mb-6">
                    Help us reach more people who need our services by sharing our mission 
                    with your network, community, and social media followers.
                  </p>
                  <a href="/contact">
                    <button className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl">
                      Contact Us
                    </button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-orange-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">501(c)(3) Registered</h4>
                <p className="text-sm text-gray-600">All donations are tax-deductible</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <DollarSign className="w-8 h-8 text-orange-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">100% to Mission</h4>
                <p className="text-sm text-gray-600">Zeffy ensures every dollar reaches our programs</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <Heart className="w-8 h-8 text-orange-600" fill="currentColor" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Secure & Safe</h4>
                <p className="text-sm text-gray-600">Bank-level encryption protects your information</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
